import { matchPath } from 'react-router-dom';
import {
  ROUTE_MINI_QUIZ_AVOIDANCES,
  ROUTE_MINI_QUIZ_BUDGET,
  ROUTE_MINI_QUIZ_BUDGET_BENEFITS,
  ROUTE_MINI_QUIZ_CUISINES,
  ROUTE_MINI_QUIZ_DISH_TYPES,
  ROUTE_MINI_QUIZ_EATING_HABITS,
  ROUTE_MINI_QUIZ_FOOD_FORMATS,
  ROUTE_MINI_QUIZ_FOOD_PREFERENCES_INTRO,
  ROUTE_MINI_QUIZ_HELLO,
  ROUTE_MINI_QUIZ_LOADING,
  ROUTE_MINI_QUIZ_MACRO_NUTRIENT,
  ROUTE_MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM,
  ROUTE_MINI_QUIZ_MEAL_FREQUENCIES,
  ROUTE_MINI_QUIZ_PROTEIN,
  ROUTE_MINI_QUIZ_SIGNUP,
  ROUTE_MINI_QUIZ_SWIPE,
  ROUTE_MINI_QUIZ_TERMINAL,
} from '@hungryroot/routes';
import { tags } from '@hungryroot/tags';
import { isMiniQuizPostSignupPending } from './miniQuizPostSignup';
import { type Quiz } from './quiz';

export interface MiniQuizRouteContext {
  isAuthenticated: boolean;
  quiz: Quiz;
}

export interface MiniQuizRouteEntry {
  path: string;
  section: null;
  shouldSkip?: (ctx: MiniQuizRouteContext) => boolean;
}

export function skipWhenAuthenticated(ctx: MiniQuizRouteContext): boolean {
  return ctx.isAuthenticated;
}

export const MINI_QUIZ_AUTH_SKIPPED_ROUTES = new Set<string>([
  ROUTE_MINI_QUIZ_LOADING,
  ROUTE_MINI_QUIZ_SIGNUP,
]);

export function filterMiniQuizRouteEntries(
  routeEntries: readonly MiniQuizRouteEntry[],
  ctx: MiniQuizRouteContext
): MiniQuizRouteEntry[] {
  return routeEntries.filter((entry) => !entry.shouldSkip?.(ctx));
}

const MINI_QUIZ_LOADING_ENTRY = {
  path: ROUTE_MINI_QUIZ_LOADING,
  section: null,
  shouldSkip: skipWhenAuthenticated,
} as const satisfies MiniQuizRouteEntry;

const MINI_QUIZ_SIGNUP_ENTRY = {
  path: ROUTE_MINI_QUIZ_SIGNUP,
  section: null,
  shouldSkip: skipWhenAuthenticated,
} as const satisfies MiniQuizRouteEntry;

const MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM_ENTRY = {
  path: ROUTE_MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM,
  section: null,
  shouldSkip: (ctx: MiniQuizRouteContext) =>
    !ctx.quiz.macroNutrient?.includes(tags.LowSodium),
} as const satisfies MiniQuizRouteEntry;

/**
 * Shared post-CYO delivery-setup tail. Appended to both variants so a
 * v1 terminal CTA and a v2 freemium CTA (cookbook nav, etc.) land on
 * the same screens. V2 signup still full-page-redirects to the cookbook
 * — the tail is a later re-entry, not the next quiz step.
 */
const MINI_QUIZ_TAIL_ROUTE_ENTRIES = [
  { path: ROUTE_MINI_QUIZ_MEAL_FREQUENCIES, section: null },
  { path: ROUTE_MINI_QUIZ_BUDGET, section: null },
  { path: ROUTE_MINI_QUIZ_BUDGET_BENEFITS, section: null },
] as const satisfies readonly MiniQuizRouteEntry[];

export const MINI_QUIZ_TAIL_ROUTES = MINI_QUIZ_TAIL_ROUTE_ENTRIES.map(
  (entry) => entry.path
);

/**
 * Mini-quiz sequences for Exp 255. Separate from `ROUTE_SEQUENCES` on
 * purpose: listing these paths there would feed `allQuizRoutes` →
 * `funnelRoutesSchema` and make the full funnel resume them, and
 * `getRedirectPath` would send a 255-enrolled visitor opening `/quiz/*`
 * into `/mini-quiz/*`.
 *
 * Keys match WEB-7627 / WEB-7628. All `section: null` — the mini-quiz
 * shell never mounts `ProgressBar`.
 */
export const MINI_QUIZ_ROUTE_SEQUENCES = {
  miniQuizVariant1: [
    { path: ROUTE_MINI_QUIZ_FOOD_FORMATS, section: null },
    { path: ROUTE_MINI_QUIZ_EATING_HABITS, section: null },
    { path: ROUTE_MINI_QUIZ_MACRO_NUTRIENT, section: null },
    MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM_ENTRY,
    { path: ROUTE_MINI_QUIZ_DISH_TYPES, section: null },
    { path: ROUTE_MINI_QUIZ_CUISINES, section: null },
    { path: ROUTE_MINI_QUIZ_PROTEIN, section: null },
    { path: ROUTE_MINI_QUIZ_AVOIDANCES, section: null },
    MINI_QUIZ_LOADING_ENTRY,
    MINI_QUIZ_SIGNUP_ENTRY,
    { path: ROUTE_MINI_QUIZ_HELLO, section: null },
    { path: ROUTE_MINI_QUIZ_FOOD_PREFERENCES_INTRO, section: null },
    { path: ROUTE_MINI_QUIZ_SWIPE, section: null },
    { path: ROUTE_MINI_QUIZ_TERMINAL, section: null },
    ...MINI_QUIZ_TAIL_ROUTE_ENTRIES,
  ],
  // WEB-7628: cookbook is the unlock, signup at the end of the quiz.
  // Pre-auth deck: personalized_options and v3 pairings/products advertise
  // optional auth (`security: [session, {}]`), so the swipe step stays in
  // this sequence and reads mini-quiz.* via QuizContext. The shared tail
  // is a later re-entry from a freemium CTA, not the step after signup.
  miniQuizVariant2: [
    { path: ROUTE_MINI_QUIZ_FOOD_FORMATS, section: null },
    { path: ROUTE_MINI_QUIZ_EATING_HABITS, section: null },
    { path: ROUTE_MINI_QUIZ_MACRO_NUTRIENT, section: null },
    MINI_QUIZ_MACRO_NUTRIENT_LOW_SODIUM_ENTRY,
    { path: ROUTE_MINI_QUIZ_DISH_TYPES, section: null },
    { path: ROUTE_MINI_QUIZ_CUISINES, section: null },
    { path: ROUTE_MINI_QUIZ_PROTEIN, section: null },
    { path: ROUTE_MINI_QUIZ_AVOIDANCES, section: null },
    { path: ROUTE_MINI_QUIZ_HELLO, section: null },
    { path: ROUTE_MINI_QUIZ_FOOD_PREFERENCES_INTRO, section: null },
    { path: ROUTE_MINI_QUIZ_SWIPE, section: null },
    MINI_QUIZ_LOADING_ENTRY,
    MINI_QUIZ_SIGNUP_ENTRY,
    ...MINI_QUIZ_TAIL_ROUTE_ENTRIES,
  ],
} as const satisfies Record<string, readonly MiniQuizRouteEntry[]>;

export type MiniQuizSequenceKey = keyof typeof MINI_QUIZ_ROUTE_SEQUENCES;

const miniQuizSequenceKeys = Object.keys(
  MINI_QUIZ_ROUTE_SEQUENCES
) as Array<MiniQuizSequenceKey>;

export const allMiniQuizRoutes = miniQuizSequenceKeys.reduce((acc, key) => {
  const sequence = MINI_QUIZ_ROUTE_SEQUENCES[key];
  sequence.forEach((entry) => {
    if (acc.indexOf(entry.path) === -1) {
      acc.push(entry.path);
    }
  });

  return acc;
}, [] as AllMiniQuizRoutes[]);

export type AllMiniQuizRoutes =
  (typeof MINI_QUIZ_ROUTE_SEQUENCES)[MiniQuizSequenceKey][number]['path'];

export function getLastNonTailMiniQuizRoute(
  routes: readonly AllMiniQuizRoutes[]
): AllMiniQuizRoutes | undefined {
  for (let index = routes.length - 1; index >= 0; index -= 1) {
    const path = routes[index];
    if (
      path !== undefined &&
      !MINI_QUIZ_TAIL_ROUTES.some((tailPath) => tailPath === path)
    ) {
      return path;
    }
  }
  return undefined;
}

function matchMiniQuizRoutePath(loc: string, route: string): boolean {
  return matchPath(loc, { path: route, exact: true, strict: false }) !== null;
}

/**
 * Paths that appear after signup in this visitor's unfiltered sequence.
 * Derive from unfiltered entries — the filtered list strips signup when
 * authenticated and would break derivation.
 */
export function getMiniQuizPostSignupRoutePaths(
  unfilteredEntries: readonly MiniQuizRouteEntry[]
): readonly string[] {
  const signupIndex = unfilteredEntries.findIndex(
    (entry) => entry.path === ROUTE_MINI_QUIZ_SIGNUP
  );
  if (signupIndex === -1) {
    return [];
  }
  return unfilteredEntries.slice(signupIndex + 1).map((entry) => entry.path);
}

/**
 * Whether a location requires authentication for this sequence. Terminal is
 * always gated (V1-only screen; V2 stale bookmarks are not in the sequence).
 */
export function isMiniQuizRouteRequiringAuth(
  loc: string,
  unfilteredEntries: readonly MiniQuizRouteEntry[]
): boolean {
  if (matchMiniQuizRoutePath(loc, ROUTE_MINI_QUIZ_TERMINAL)) {
    return true;
  }
  return getMiniQuizPostSignupRoutePaths(unfilteredEntries).some((route) =>
    matchMiniQuizRoutePath(loc, route)
  );
}

export function getMiniQuizImmediatePostSignupRoute(
  unfilteredEntries: readonly MiniQuizRouteEntry[]
): string | undefined {
  const signupIndex = unfilteredEntries.findIndex(
    (entry) => entry.path === ROUTE_MINI_QUIZ_SIGNUP
  );
  if (signupIndex === -1) {
    return undefined;
  }
  return unfilteredEntries[signupIndex + 1]?.path;
}

/**
 * Signup pushes to the next route before auth flips. While saved nav still
 * points at signup, allow that one forward step only.
 */
export function isFreshMiniQuizSignupTransition({
  path,
  savedNavPathname,
  unfilteredEntries,
}: {
  path: string;
  savedNavPathname: string | undefined;
  unfilteredEntries: readonly MiniQuizRouteEntry[];
}): boolean {
  const immediateNext = getMiniQuizImmediatePostSignupRoute(unfilteredEntries);
  if (
    immediateNext === undefined ||
    !matchMiniQuizRoutePath(path, immediateNext)
  ) {
    return false;
  }
  if (isMiniQuizPostSignupPending()) {
    return true;
  }
  if (savedNavPathname === undefined) {
    return false;
  }
  return matchMiniQuizRoutePath(savedNavPathname, ROUTE_MINI_QUIZ_SIGNUP);
}
